import { PageHero, PrimaryButton } from "@/components/site/Bits";
import { Reveal } from "@/components/site/Reveal";
import { MessageSquare, PenTool, CheckCircle2, Factory, Cpu, PackageCheck, ArrowRight, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const ICONS = [MessageSquare, PenTool, CheckCircle2, Factory, Cpu, PackageCheck];

export default function HowItWorks() {
  const { t } = useI18n();
  const steps = t("how.steps") || [];
  const labels = t("how.labels") || [];

  return (
    <div>
      <PageHero eyebrow={t("how.eyebrow")} title={t("how.title")} subtitle={t("how.subtitle")} />

      {/* Progression path */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 -mt-2 sm:-mt-4">
        <Reveal>
          <div
            data-testid="how-progression"
            className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {labels.map((l, i) => (
              <div key={i} className="flex items-center gap-2 sm:gap-3 shrink-0">
                <span className="px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium text-white/90 border border-white/10 bg-white/[0.04] whitespace-nowrap">
                  {l}
                </span>
                {i < labels.length - 1 && <ArrowRight className="w-4 h-4 text-cyan-400 shrink-0" />}
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Timeline */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="relative">
          {/* Connecting gradient line */}
          <span
            aria-hidden
            className="absolute left-[27px] sm:left-[31px] top-6 bottom-6 w-px bg-gradient-to-b from-cyan-400/60 via-blue-500/40 to-fuchsia-500/60"
          />

          <div className="space-y-6 sm:space-y-8">
            {steps.map((s, i) => {
              const Icon = ICONS[i] || MessageSquare;
              return (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="relative flex items-start gap-5 sm:gap-7" data-testid={`how-step-${i + 1}`}>
                    {/* Node */}
                    <div className="relative shrink-0">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-brand-gradient flex items-center justify-center shadow-lg shadow-blue-500/25 ring-4 ring-[#05050A]">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={1.75} />
                      </div>
                    </div>

                    {/* Card */}
                    <div className="glass rounded-3xl p-6 sm:p-8 flex-1 card-hover">
                      <div className="flex items-center gap-3">
                        <span className="font-display font-black text-2xl sm:text-3xl text-gradient leading-none">
                          0{i + 1}
                        </span>
                        <h3 className="font-display font-semibold text-lg sm:text-xl text-white">{s.t}</h3>
                      </div>
                      <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Arrival marker */}
          <Reveal delay={0.1}>
            <div className="relative flex items-center gap-5 sm:gap-7 mt-6 sm:mt-8">
              <div className="w-14 sm:w-16 flex justify-center">
                <ChevronDown className="w-6 h-6 text-cyan-400 animate-bounce" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-10 sm:mt-12 text-center">
          <PrimaryButton to="/contact" testid="how-cta">{t("buttons.talkProject")}</PrimaryButton>
        </div>
      </section>
    </div>
  );
}
